import { ArrowPathIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import React from "react";

import { Button, Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

import { EhrSyncGatedNotice } from "@components/EHRConnect/EhrSyncGatedNotice";
import { useClientListImport } from "@hooks/useClientListImport";
import { OnboardingShell } from "@pages/Onboarding/OnboardingShell";
import { ConnectEHRRequiredDialog } from "@pages/Onboarding/dialogs/ConnectEHRRequiredDialog";
import {
  isDirectIntegrationOrgType,
  isEhrConnected,
  isStatusMaskedOrgType,
} from "@pages/Onboarding/ehrConnectionState";
import { useAdminData } from "@providers/AdminDataProvider";
import { useEHRConnection } from "@providers/EHRConnectionProvider";

interface ImportClientsStepProps {
  onBack: () => void;
  onFinish: () => void;
  // Skip importing now and go straight to the homepage (clients can be imported later).
  onSkip: () => void;
  // Preview override (URL-driven): force the connect-required dialog open
  // regardless of org EHR-compatibility, so it's reviewable like the rest of the flow.
  forceConnectRequiredDialog?: boolean;
  currentStep: number;
  totalSteps: number;
}

// Step 4 — Import the clinic's clients from the connected EHR. Reuses the
// existing useClientListImport orchestration (start + status polling). "Finish"
// lands the BCBA on the homepage; "Skip for now" does too, for those who'd
// rather import later. Matches Figma frame 204:386.
//
// Importing depends on an active EHR connection. If an EHR-compatible user who
// skipped the Connect-EHR step clicks Import, we surface ConnectEHRRequiredDialog
// instead of starting the import (connect now, or dismiss and stay on this step).
export const ImportClientsStep: React.FC<ImportClientsStepProps> = ({
  onBack,
  onFinish,
  onSkip,
  forceConnectRequiredDialog = false,
  currentStep,
  totalSteps,
}) => {
  const [imported, setImported] = React.useState(false);
  // Seeded from the preview flag so `?dialog=connect-ehr` opens it, but kept as
  // local state so the dialog can actually be dismissed in that preview mode too.
  const [showConnectRequired, setShowConnectRequired] = React.useState(forceConnectRequiredDialog);
  const { organizationType, isEhrSyncCleared } = useAdminData();
  // Read before useClientListImport rather than after it (where main has
  // this line): the hook now takes usesOrgSession.
  const { isEHREnabled, ehrConnectionStatus, openEHRLogin, usesOrgSession } = useEHRConnection();
  // Direct-integration orgs (Passage / Therapz / Hipp Health) connect at the
  // org level (no per-user credentials), so they skip the connect-required
  // gate below regardless of whether their status is polled.
  const isDirectIntegrationOrg = isDirectIntegrationOrgType(organizationType);
  // Hipp Health's backend status is still hardcoded CONNECTED and never
  // transitions through IMPORTING_CLIENT_LIST, so treat mutation success as
  // import success instead of polling for a status that never arrives (which
  // would otherwise hit the import timeout). Passage Health and Therapz now
  // report real status (PROA-2515) and poll like any other EHR.
  const { isImporting, startImport } = useClientListImport({
    // INT-36: CentralReach orgs on shared credentials queue the import
    // on the org-wide session instead of signalling a per-provider
    // workflow, and completion is read off the request row.
    usesOrgSession,
    // PROA-2515: only the orgs whose status is masked skip the poll now —
    // Passage and Therapz report real status and poll like any other EHR.
    waitForStatusTransition: !isStatusMaskedOrgType(organizationType),
    isEhrSyncCleared,
    onSuccess: () => setImported(true),
  });

  const handleImportClick = () => {
    // Scraper EHR org that hasn't connected yet → explain the dependency before
    // importing. Direct-integration orgs (Passage / Therapz / Hipp Health) connect
    // at the org level (no per-user credentials) and are treated as connected, so
    // they skip this gate. Orgs with no connectable EHR also fall through.
    if (isEHREnabled && !isDirectIntegrationOrg && !isEhrConnected(ehrConnectionStatus)) {
      setShowConnectRequired(true);
      return;
    }
    startImport();
  };

  let importStatus: React.ReactNode;
  if (imported) {
    importStatus = (
      <span className="flex items-center gap-2 text-sm font-medium text-green-700">
        <CheckCircleIcon className="h-5 w-5" />
        Clients imported
      </span>
    );
  } else if (isImporting) {
    importStatus = (
      <span className="flex items-center gap-1.5 text-sm text-text-tertiary">
        <ArrowPathIcon className="animate-slow-spin h-4 w-4" />
        Importing…
      </span>
    );
  } else {
    importStatus = (
      <div className="flex flex-col items-start gap-1">
        <Button
          text="Import clients"
          variant="primary"
          buttonStyle="border"
          disabled={!isEhrSyncCleared}
          onClick={handleImportClick}
        />
        {!isEhrSyncCleared && <EhrSyncGatedNotice />}
      </div>
    );
  }

  return (
    <OnboardingShell
      onContinue={onFinish}
      onBack={onBack}
      onSkip={onSkip}
      continueLabel="Finish"
      currentStep={currentStep}
      totalSteps={totalSteps}
    >
      <Headline size="medium">Import your clients</Headline>
      <Paragraph size="small" className="mt-2 text-text-secondary">
        By importing your EHR/data collection platform clients, you&apos;ll be able to directly access their data
        seamlessly during your report creation
      </Paragraph>

      <div className="mt-8">
        <p className="text-base font-semibold text-text-primary">Import your client data</p>
        <Paragraph size="small" className="mt-1 text-text-secondary">
          Don&apos;t worry you can also do this later
        </Paragraph>

        <div className="mt-4">{importStatus}</div>
      </div>

      <ConnectEHRRequiredDialog
        open={showConnectRequired}
        onConnectEHR={() => {
          // Close this dialog and hand off to the EHR login flow (inline-error
          // mode, no header intro); the user lands back here connected and imports.
          setShowConnectRequired(false);
          openEHRLogin(undefined, false, true);
        }}
        onDismiss={() => setShowConnectRequired(false)}
      />
    </OnboardingShell>
  );
};
